import { DatePickerTR } from "@/components/DatePickerTR";
import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  CreditCard, Plus, Trash2, Loader2, Save, ChevronDown, Landmark, Utensils,
  Route, MonitorSmartphone, FlaskConical, CheckCircle2, XCircle, Zap, Wallet,
  Coins, RotateCcw, Ban, Rocket, Sparkles,
} from "lucide-react";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { InfoBox } from "@/components/InfoBox";
import { curSym } from "@/lib/company";

const KIND_LABEL = { bank_pos: "Banka POS", meal_card: "Yemek Kartı" };
const KIND_ICON = { bank_pos: Landmark, meal_card: Utensils };
const STATUS_BADGE = {
  captured: ["Onaylı", "bg-emerald-100 text-emerald-700"],
  declined: ["Red", "bg-red-100 text-red-700"],
  voided: ["İptal", "bg-slate-200 text-slate-600"],
  refunded: ["İade", "bg-amber-100 text-amber-700"],
};

function Section({ children }) {
  return <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">{children}</div>;
}
const selCls = "h-9 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green";
const tl = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 }) + " " + curSym();

export default function PaymentCenter() {
  const [sub, setSub] = useState("providers");
  const [catalog, setCatalog] = useState({ providers: [] });
  const [overview, setOverview] = useState(null);
  const [providers, setProviders] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [rules, setRules] = useState([]);
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exp, setExp] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, o, p, t, r, i] = await Promise.all([
        api.get("/payments/catalog"), api.get("/payments/overview"),
        api.get("/payments/providers"), api.get("/payments/terminals"),
        api.get("/payments/routing-rules"), api.get("/payments/intents"),
      ]);
      setCatalog(c.data); setOverview(o.data); setProviders(p.data);
      setTerminals(t.data); setRules(r.data); setIntents(i.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const termName = (id) => terminals.find((t) => t.id === id)?.name || "—";
  const provName = (id) => providers.find((p) => p.id === id)?.name || "—";
  const refreshIntents = () => { api.get("/payments/intents").then((r) => setIntents(r.data)).catch(() => {}); api.get("/payments/overview").then((r) => setOverview(r.data)).catch(() => {}); };

  // ---- providers ----
  const [newType, setNewType] = useState("garanti");
  const addProvider = async () => {
    try { await api.post("/payments/providers", { type_key: newType }); toast.success("Sağlayıcı eklendi (SANDBOX)"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveProvider = async (p) => {
    try {
      await api.put(`/payments/providers/${p.id}`, {
        name: p.name, brand_code: p.brand_code, enabled: p.enabled,
        merchant_no: p.merchant_no, terminal_no: p.terminal_no, api_key: p.api_key,
        api_secret: p._secret_dirty ? p.api_secret : undefined,
        commission_pct: p.commission_pct, terminal_id: p.terminal_id || "", mode: p.mode || "sandbox",
      });
      toast.success("Kaydedildi"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delProvider = async (id) => { try { await api.delete(`/payments/providers/${id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const patchProvider = (id, patch) => setProviders((ps) => ps.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const [golive, setGolive] = useState({}); // pid -> canlıya alma kontrol listesi
  const checkGolive = async (pid) => {
    try { const { data } = await api.get(`/payments/providers/${pid}/golive`); setGolive((g) => ({ ...g, [pid]: data })); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ---- terminals ----
  const [tForm, setTForm] = useState({ name: "", model: "" });
  const addTerminal = async () => {
    if (!tForm.name.trim()) return toast.error("Terminal adı gerekli");
    try { await api.post("/payments/terminals", tForm); setTForm({ name: "", model: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleTerminal = async (t) => { try { await api.put(`/payments/terminals/${t.id}`, { enabled: !t.enabled }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const delTerminal = async (id) => { try { await api.delete(`/payments/terminals/${id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  // ---- rules ----
  const [rForm, setRForm] = useState({ name: "", priority: 100, payment_kind: "", brand_code: "", terminal_id: "", min_amount: "", max_amount: "", target_provider_id: "" });
  const addRule = async () => {
    if (!rForm.name.trim()) return toast.error("Kural adı gerekli");
    if (!rForm.target_provider_id) return toast.error("Hedef sağlayıcı seçin");
    try {
      await api.post("/payments/routing-rules", {
        name: rForm.name, priority: +rForm.priority || 100, target_provider_id: rForm.target_provider_id,
        match: {
          payment_kind: rForm.payment_kind || null, brand_code: rForm.brand_code || null,
          terminal_id: rForm.terminal_id || null,
          min_amount: rForm.min_amount === "" ? null : +rForm.min_amount,
          max_amount: rForm.max_amount === "" ? null : +rForm.max_amount,
        },
      });
      setRForm({ name: "", priority: 100, payment_kind: "", brand_code: "", terminal_id: "", min_amount: "", max_amount: "", target_provider_id: "" });
      toast.success("Yönlendirme kuralı eklendi"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleRule = async (r) => { try { await api.put(`/payments/routing-rules/${r.id}`, { enabled: !r.enabled }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const delRule = async (id) => { try { await api.delete(`/payments/routing-rules/${id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const addSampleRules = async () => {
    const bank = providers.find((p) => p.kind === "bank_pos");
    const meal = providers.find((p) => p.kind === "meal_card");
    if (!bank && !meal) return toast.error("Önce en az bir sağlayıcı ekleyin");
    try {
      if (bank) await api.post("/payments/routing-rules", { name: `Yüksek tutar → ${bank.name}`, priority: 1, target_provider_id: bank.id, match: { payment_kind: "card", min_amount: 500 } });
      if (meal) await api.post("/payments/routing-rules", { name: `${meal.name} markası`, priority: 5, target_provider_id: meal.id, match: { payment_kind: "meal_card", brand_code: meal.brand_code || null } });
      toast.success("Örnek kurallar eklendi"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ---- checkout (demo) ----
  const [co, setCo] = useState({ amount: "", payment_kind: "card", brand_code: "", terminal_id: "", force: false, split: false, card_amount: "", meal_amount: "", meal_brand: "" });
  const [coBusy, setCoBusy] = useState(false);
  const [coResult, setCoResult] = useState(null);
  const mealProviders = providers.filter((p) => p.kind === "meal_card" && p.enabled !== false);
  const runCheckout = async () => {
    if (!(+co.amount > 0)) return toast.error("Geçerli bir tutar girin");
    setCoBusy(true); setCoResult(null);
    try {
      const { data } = await api.post("/payments/checkout", {
        amount: +co.amount, payment_kind: co.payment_kind,
        brand_code: co.payment_kind === "meal_card" ? (co.brand_code || null) : null,
        terminal_id: co.terminal_id || null, force: co.force ? "decline" : null,
      });
      setCoResult(data);
      if (data.status === "captured") toast.success(`Onaylandı → ${data.provider_name}`);
      else toast.error(`Reddedildi → ${data.message}`);
      refreshIntents();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCoBusy(false); }
  };
  const [splitResult, setSplitResult] = useState(null);
  const runSplit = async () => {
    const card = +co.card_amount, meal = +co.meal_amount;
    if (!(card > 0) || !(meal > 0)) return toast.error("Kart ve yemek kartı tutarlarının ikisi de 0'dan büyük olmalı");
    setCoBusy(true); setSplitResult(null); setCoResult(null);
    try {
      const { data } = await api.post("/payments/checkout-split", {
        parts: [
          { amount: card, payment_kind: "card" },
          { amount: meal, payment_kind: "meal_card", brand_code: co.meal_brand || null },
        ],
        terminal_id: co.terminal_id || null, force: co.force ? "decline" : null,
      });
      setSplitResult(data);
      if (data.all_ok) toast.success(`Bölünmüş ödeme onaylandı → ${tl(data.total)}`);
      else toast.error(`Bölünmüş ödeme başarısız → ${data.failed?.message || "reddedildi"}`);
      refreshIntents();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCoBusy(false); }
  };
  const reverseIntent = async (id, kind) => {
    try { await api.post(`/payments/intents/${id}/${kind}`); toast.success(kind === "refund" ? "İade edildi (SANDBOX)" : "İptal edildi (SANDBOX)"); refreshIntents(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ---- reconciliation ----
  const [recon, setRecon] = useState(null);
  const [reconRange, setReconRange] = useState({ from: "", to: "" });
  const [reconBusy, setReconBusy] = useState(false);
  const loadRecon = useCallback(async () => {
    setReconBusy(true);
    try {
      const { data } = await api.get("/payments/reconciliation", { params: { date_from: reconRange.from || undefined, date_to: reconRange.to || undefined } });
      setRecon(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setReconBusy(false); }
  }, [reconRange.from, reconRange.to]);
  useEffect(() => { if (sub === "reconciliation" && !recon) loadRecon(); }, [sub, recon, loadRecon]);

  if (loading) return <div className="rounded-lg bg-white p-8 text-center text-sm text-slate-400 ring-1 ring-inset ring-slate-200"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>;

  const subs = [
    { id: "providers", label: "Sağlayıcılar", icon: CreditCard },
    { id: "terminals", label: "Terminaller", icon: MonitorSmartphone },
    { id: "rules", label: "Yönlendirme", icon: Route },
    { id: "checkout", label: "Kasiyer (Demo)", icon: Wallet },
    { id: "reconciliation", label: "Mutabakat", icon: Coins },
  ];

  return (
    <div className="space-y-4" data-testid="payment-center">
      <Section>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-green"><CreditCard className="h-5 w-5 text-white" /></div>
            <div>
              <p className="font-heading text-lg font-bold text-slate-900">Ödeme Merkezi</p>
              <p className="text-sm text-slate-500">Çok bankalı POS + çok yemek kartı, tek terminal. Kasiyer tek ekran görür.</p>
            </div>
          </div>
          <span data-testid="pc-mode-badge" className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800 ring-1 ring-inset ring-amber-300">
            <FlaskConical className="h-3.5 w-3.5" /> SANDBOX · Simülasyon
          </span>
        </div>
        {overview && (
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="pc-overview">
            <Stat label="Banka POS" value={`${overview.banks_enabled}/${overview.banks}`} />
            <Stat label="Yemek Kartı" value={`${overview.meal_enabled}/${overview.meal_cards}`} />
            <Stat label="Terminal" value={overview.terminals} />
            <Stat label="İşlem" value={overview.intents} />
          </div>
        )}
      </Section>

      <InfoBox testid="pc-help" title="Ödeme Merkezi nedir? (kısa anlatım)"
        example="3 farklı bankanın POS'u ve 2 yemek kartın varsa hepsini buraya ekle. '500 ₺ üstü → Garanti POS' gibi bir kural yaz. Kasiyer sadece tutarı girer; sistem doğru cihaza kendi gönderir.">
        Bu ekran, mağazandaki tüm <b>banka POS cihazlarını</b> ve <b>yemek kartı</b> anlaşmalarını tek yerde toplar.
        <b>Sağlayıcılar</b>: kullandığın bankaları/kartları eklersin. <b>Terminaller</b>: fiziksel POS cihazların.
        <b>Yönlendirme Kuralları</b>: hangi ödemenin hangi cihaza gideceğini belirlersin (ör. tutara veya karta göre).
        Şu an <b>SANDBOX</b> (deneme) modundadır — gerçek para çekilmez, güvenle deneyebilirsin.
      </InfoBox>

      <div className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="pc-setup-guide">
        <p className="mb-3 text-sm font-bold text-slate-800">Kurulum Rehberi — 4 adımda hazır</p>
        <div className="grid gap-3 sm:grid-cols-4">
          {[["1", "Sağlayıcı ekle", "Kullandığın banka POS ve yemek kartlarını 'Sağlayıcılar'dan ekle."], ["2", "Terminal tanımla", "Fiziksel POS cihazlarını 'Terminaller'de tanımla (opsiyonel)."], ["3", "Yönlendirme kuralı", "'Kurallar'da hangi ödemenin nereye gideceğini yaz (ör. 500₺ üstü → Garanti)."], ["4", "Kasiyer dener", "'Kasiyer (Demo)'da tutar + tip seç; motor doğru cihaza yönlendirir."]].map(([n, t, dsc]) => (
            <div key={n} className="rounded-lg bg-slate-50 p-3 ring-1 ring-inset ring-slate-100" data-testid={`pc-guide-step-${n}`}>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-green text-xs font-bold text-white">{n}</span>
                <span className="text-sm font-semibold text-slate-800">{t}</span>
              </div>
              <p className="text-[11px] leading-relaxed text-slate-500">{dsc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="inline-flex flex-wrap rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200" data-testid="pc-subtabs">
        {subs.map((s) => (
          <button key={s.id} data-testid={`pc-subtab-${s.id}`} onClick={() => setSub(s.id)}
            className={`inline-flex items-center gap-1.5 rounded-sm px-3.5 py-1.5 text-sm font-semibold transition-colors ${sub === s.id ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"}`}>
            <s.icon className="h-4 w-4" /> {s.label}
          </button>
        ))}
      </div>

      {/* ---------------- Sağlayıcılar ---------------- */}
      {sub === "providers" && (
        <>
        <Section>
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <div className="min-w-[220px] flex-1">
              <Label className="text-[11px]">Sağlayıcı türü ekle</Label>
              <select data-testid="pc-add-provider-type" value={newType} onChange={(e) => setNewType(e.target.value)} className={`mt-1 ${selCls}`}>
                <optgroup label="Banka POS">
                  {catalog.providers.filter((c) => c.kind === "bank_pos").map((c) => <option key={c.type_key} value={c.type_key}>{c.label}</option>)}
                </optgroup>
                <optgroup label="Yemek Kartı">
                  {catalog.providers.filter((c) => c.kind === "meal_card").map((c) => <option key={c.type_key} value={c.type_key}>{c.label}</option>)}
                </optgroup>
              </select>
            </div>
            <Button data-testid="pc-add-provider-btn" onClick={addProvider} className="bg-brand-green text-white hover:bg-brand-green-600"><Plus className="mr-1.5 h-4 w-4" /> Ekle</Button>
          </div>
          {providers.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">Henüz sağlayıcı yok. Banka POS ve/veya yemek kartı ekleyin.</p>
          ) : (
            <div className="space-y-2.5">
              {providers.map((p) => {
                const KIcon = KIND_ICON[p.kind] || CreditCard;
                const open = exp === p.id;
                return (
                  <div key={p.id} data-testid={`pc-provider-${p.id}`} className="rounded-lg border border-slate-200 p-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <Switch data-testid={`pc-provider-enabled-${p.id}`} checked={p.enabled !== false} onCheckedChange={(v) => patchProvider(p.id, { enabled: v })} />
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"><KIcon className="h-3 w-3" /> {KIND_LABEL[p.kind]}</span>
                      <Input data-testid={`pc-provider-name-${p.id}`} value={p.name || ""} onChange={(e) => patchProvider(p.id, { name: e.target.value })} className="flex-1 min-w-[140px]" />
                      <div className="flex items-center gap-1"><Input type="number" data-testid={`pc-provider-commission-${p.id}`} value={p.commission_pct ?? 0} onChange={(e) => patchProvider(p.id, { commission_pct: +e.target.value })} className="w-16 text-center" title="Komisyon %" /><span className="text-sm font-semibold text-slate-500">%</span></div>
                      <button type="button" data-testid={`pc-provider-expand-${p.id}`} onClick={() => setExp(open ? null : p.id)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-brand-green hover:bg-brand-green/5"><Landmark className="h-3.5 w-3.5" /> Detay <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /></button>
                      <Button size="sm" data-testid={`pc-provider-save-${p.id}`} onClick={() => saveProvider(p)} className="bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-1 h-3.5 w-3.5" /> Kaydet</Button>
                      <ConfirmDelete testid={`pc-provider-del-${p.id}`} title="Ödeme sağlayıcısını sil?"
                        description={`"${p.name}" kalıcı olarak silinecek. Bu sağlayıcıya bağlı yönlendirme kuralları çalışmayabilir.`}
                        onConfirm={() => delProvider(p.id)}
                        trigger={<button data-testid={`pc-provider-delete-${p.id}`} className="rounded-sm p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>} />
                    </div>
                    {open && (
                      <div className="mt-3 grid gap-2.5 border-t border-slate-100 pt-3 sm:grid-cols-2" data-testid={`pc-provider-detail-${p.id}`}>
                        <div><Label className="text-[11px]">Marka Kodu</Label><Input data-testid={`pc-provider-brand-${p.id}`} value={p.brand_code || ""} onChange={(e) => patchProvider(p.id, { brand_code: e.target.value })} className="mt-1 font-mono" /></div>
                        <div><Label className="text-[11px]">Bağlı Terminal</Label>
                          <select data-testid={`pc-provider-terminal-${p.id}`} value={p.terminal_id || ""} onChange={(e) => patchProvider(p.id, { terminal_id: e.target.value })} className={`mt-1 ${selCls}`}>
                            <option value="">— (herhangi)</option>
                            {terminals.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                        </div>
                        <div><Label className="text-[11px]">Üye İşyeri No</Label><Input data-testid={`pc-provider-merchant-${p.id}`} value={p.merchant_no || ""} onChange={(e) => patchProvider(p.id, { merchant_no: e.target.value })} className="mt-1 font-mono" /></div>
                        <div><Label className="text-[11px]">Terminal / POS No</Label><Input data-testid={`pc-provider-terminalno-${p.id}`} value={p.terminal_no || ""} onChange={(e) => patchProvider(p.id, { terminal_no: e.target.value })} className="mt-1 font-mono" /></div>
                        <div><Label className="text-[11px]">API Anahtarı</Label><Input data-testid={`pc-provider-apikey-${p.id}`} value={p.api_key || ""} onChange={(e) => patchProvider(p.id, { api_key: e.target.value })} className="mt-1 font-mono" /></div>
                        <div><Label className="text-[11px]">API Sırrı {p.api_secret_set && <span className="text-[10px] font-semibold text-emerald-600">(kayıtlı)</span>}</Label><Input data-testid={`pc-provider-apisecret-${p.id}`} type="password" autoComplete="new-password" value={p.api_secret || ""} onChange={(e) => patchProvider(p.id, { api_secret: e.target.value, _secret_dirty: true })} placeholder={p.api_secret_set ? "•••• (değiştirmek için yazın)" : "API Secret"} className="mt-1 font-mono" /></div>
                        <div className="sm:col-span-2 mt-1 rounded-lg border border-slate-200 bg-slate-50 p-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Rocket className="h-4 w-4 text-brand-green" /> Çalışma Modu (Faz 3)</span>
                            <div className="flex items-center gap-1">
                              <button data-testid={`pc-provider-mode-sandbox-${p.id}`} onClick={() => patchProvider(p.id, { mode: "sandbox" })} className={`rounded-full px-3 py-1 text-xs font-bold ${(p.mode || "sandbox") === "sandbox" ? "bg-amber-100 text-amber-700" : "bg-white text-slate-400 ring-1 ring-inset ring-slate-200"}`}><FlaskConical className="mr-1 inline h-3 w-3" /> SANDBOX</button>
                              <button data-testid={`pc-provider-mode-live-${p.id}`} onClick={() => patchProvider(p.id, { mode: "live" })} className={`rounded-full px-3 py-1 text-xs font-bold ${p.mode === "live" ? "bg-emerald-100 text-emerald-700" : "bg-white text-slate-400 ring-1 ring-inset ring-slate-200"}`}><Zap className="mr-1 inline h-3 w-3" /> CANLI</button>
                            </div>
                          </div>
                          {p.mode === "live" && <p className="mt-1 text-[11px] font-semibold text-amber-600">CANLI seçili — sertifikasyon tamamlanmadan gerçek tahsilat YAPILMAZ (işlem reddedilir). Kalıcı olması için "Kaydet".</p>}
                          <button data-testid={`pc-provider-golive-${p.id}`} onClick={() => checkGolive(p.id)} className="mt-2 inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-brand-green ring-1 ring-inset ring-brand-green/30 hover:bg-brand-green/5"><CheckCircle2 className="h-3.5 w-3.5" /> Canlıya Alma Kontrolü</button>
                          {golive[p.id] && (
                            <ul className="mt-2 space-y-1" data-testid={`pc-golive-list-${p.id}`}>
                              {golive[p.id].items.map((it) => (
                                <li key={it.key} className="flex items-center gap-2 text-[12px] text-slate-600">
                                  {it.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <XCircle className="h-3.5 w-3.5 text-slate-300" />} {it.label}
                                </li>
                              ))}
                              <li className={`mt-1 text-[11px] font-bold ${golive[p.id].fields_ready ? "text-emerald-600" : "text-amber-600"}`}>{golive[p.id].fields_ready ? "Alanlar tamam — sertifikasyon bekleniyor." : "Eksik alanlar var."}</li>
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>
        {/* Canlıya Alma hazırlık kartı */}
        <Section>
          <div className="flex items-center gap-2"><Rocket className="h-5 w-5 text-brand-green" /><p className="font-heading text-sm font-bold text-slate-900">Canlıya Alma (Hazırlık)</p><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">planlı</span></div>
          <p className="mt-1 text-sm text-slate-500">Gerçek tahsilat için üye işyeri sertifikasyonu gerekir. Hazır olduğunuzda sırasıyla:</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-600" data-testid="pc-golive-checklist">
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-green" /> Banka üye işyeri (VPOS) + terminal/POS numaraları</li>
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-green" /> Yemek kartı entegrasyon anahtarları</li>
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-green" /> Fiziksel terminal OPI/ECR bağlantısı</li>
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-green" /> Sertifikasyon testleri onayı → canlı moda geçiş</li>
          </ul>
        </Section>
        </>
      )}

      {/* ---------------- Terminaller ---------------- */}
      {sub === "terminals" && (
        <Section>
          <p className="mb-3 text-sm text-slate-500">Fiziksel terminaller — tek cihaz, arkasında çok sağlayıcı.</p>
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[160px]"><Label className="text-[11px]">Terminal Adı</Label><Input data-testid="pc-terminal-name" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} placeholder="Kasa 1 Terminali" className="mt-1" /></div>
            <div className="flex-1 min-w-[140px]"><Label className="text-[11px]">Model</Label><Input data-testid="pc-terminal-model" value={tForm.model} onChange={(e) => setTForm({ ...tForm, model: e.target.value })} placeholder="Ingenico / Verifone / PAX" className="mt-1" /></div>
            <Button data-testid="pc-add-terminal-btn" onClick={addTerminal} className="bg-brand-green text-white hover:bg-brand-green-600"><Plus className="mr-1.5 h-4 w-4" /> Ekle</Button>
          </div>
          {terminals.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">Henüz terminal yok.</p> : (
            <div className="space-y-2">
              {terminals.map((t) => (
                <div key={t.id} data-testid={`pc-terminal-${t.id}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-2.5">
                  <Switch data-testid={`pc-terminal-enabled-${t.id}`} checked={t.enabled !== false} onCheckedChange={() => toggleTerminal(t)} />
                  <MonitorSmartphone className="h-4 w-4 text-slate-400" />
                  <span className="flex-1 font-medium text-slate-800">{t.name}</span>
                  <span className="text-xs text-slate-400">{t.model || "—"}</span>
                  <ConfirmDelete testid={`pc-terminal-del-${t.id}`} title="Terminali sil?"
                    description={`"${t.name}" terminali kalıcı olarak silinecek.`}
                    onConfirm={() => delTerminal(t.id)}
                    trigger={<button data-testid={`pc-terminal-delete-${t.id}`} className="rounded-sm p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>} />
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* ---------------- Yönlendirme ---------------- */}
      {sub === "rules" && (
        <Section>
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-sm text-slate-500">Öncelik (küçük = önce) sırasıyla; eşleşme yoksa ilk etkin sağlayıcı.</p>
            <Button size="sm" variant="outline" data-testid="pc-add-sample-rules" onClick={addSampleRules}><Sparkles className="mr-1.5 h-4 w-4" /> Örnek Kural Ekle</Button>
          </div>
          <div className="mb-3 grid gap-2 rounded-lg bg-slate-50 p-3 ring-1 ring-inset ring-slate-200 sm:grid-cols-2 lg:grid-cols-4">
            <div><Label className="text-[11px]">Kural Adı</Label><Input data-testid="pc-rule-name" value={rForm.name} onChange={(e) => setRForm({ ...rForm, name: e.target.value })} placeholder="Yüksek tutar → Garanti" className="mt-1" /></div>
            <div><Label className="text-[11px]">Öncelik</Label><Input type="number" data-testid="pc-rule-priority" value={rForm.priority} onChange={(e) => setRForm({ ...rForm, priority: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-[11px]">Ödeme Türü</Label>
              <select data-testid="pc-rule-kind" value={rForm.payment_kind} onChange={(e) => setRForm({ ...rForm, payment_kind: e.target.value })} className={`mt-1 ${selCls}`}>
                <option value="">Hepsi</option><option value="card">Banka Kartı</option><option value="meal_card">Yemek Kartı</option>
              </select>
            </div>
            <div><Label className="text-[11px]">Marka Kodu (yemek)</Label><Input data-testid="pc-rule-brand" value={rForm.brand_code} onChange={(e) => setRForm({ ...rForm, brand_code: e.target.value })} placeholder="Örn. MLTN" className="mt-1 font-mono" /></div>
            <div><Label className="text-[11px]">Min Tutar ({curSym()})</Label><Input type="number" data-testid="pc-rule-min" value={rForm.min_amount} onChange={(e) => setRForm({ ...rForm, min_amount: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-[11px]">Maks Tutar ({curSym()})</Label><Input type="number" data-testid="pc-rule-max" value={rForm.max_amount} onChange={(e) => setRForm({ ...rForm, max_amount: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-[11px]">Terminal</Label>
              <select data-testid="pc-rule-terminal" value={rForm.terminal_id} onChange={(e) => setRForm({ ...rForm, terminal_id: e.target.value })} className={`mt-1 ${selCls}`}>
                <option value="">Hepsi</option>{terminals.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div><Label className="text-[11px]">Hedef Sağlayıcı *</Label>
              <select data-testid="pc-rule-target" value={rForm.target_provider_id} onChange={(e) => setRForm({ ...rForm, target_provider_id: e.target.value })} className={`mt-1 ${selCls}`}>
                <option value="">Seçin…</option>{providers.map((p) => <option key={p.id} value={p.id}>{`${p.name} (${KIND_LABEL[p.kind]})`}</option>)}
              </select>
            </div>
            <div className="flex items-end"><Button data-testid="pc-add-rule-btn" onClick={addRule} className="w-full bg-brand-green text-white hover:bg-brand-green-600"><Plus className="mr-1.5 h-4 w-4" /> Kural Ekle</Button></div>
          </div>
          {rules.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">Henüz kural yok — motor varsayılan (ilk etkin sağlayıcı) kullanır.</p> : (
            <div className="space-y-2">
              {rules.map((r) => (
                <div key={r.id} data-testid={`pc-rule-${r.id}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-2.5 text-sm">
                  <Switch data-testid={`pc-rule-enabled-${r.id}`} checked={r.enabled !== false} onCheckedChange={() => toggleRule(r)} />
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">#{r.priority}</span>
                  <span className="font-semibold text-slate-800">{r.name}</span>
                  <span className="text-xs text-slate-500">
                    {r.match?.payment_kind ? (r.match.payment_kind === "card" ? "Kart" : "Yemek") : "Hepsi"}
                    {r.match?.brand_code ? ` · ${r.match.brand_code}` : ""}
                    {r.match?.min_amount != null ? ` · ≥${r.match.min_amount}${curSym()}` : ""}
                    {r.match?.max_amount != null ? ` · ≤${r.match.max_amount}${curSym()}` : ""}
                    {r.match?.terminal_id ? ` · ${termName(r.match.terminal_id)}` : ""}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-brand-green"><Zap className="h-3.5 w-3.5" /> {provName(r.target_provider_id)}</span>
                  <ConfirmDelete testid={`pc-rule-del-${r.id}`} title="Yönlendirme kuralını sil?"
                    description={`"${r.name}" kuralı kalıcı olarak silinecek. Bu ödemeler bundan sonra varsayılan sağlayıcıya yönlenir.`}
                    onConfirm={() => delRule(r.id)}
                    trigger={<button data-testid={`pc-rule-delete-${r.id}`} className="rounded-sm p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>} />
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* ---------------- Kasiyer Checkout (Demo) ---------------- */}
      {sub === "checkout" && (
        <div className="space-y-4">
          <Section>
            <div className="mb-3 flex items-center gap-2">
              <Wallet className="h-5 w-5 text-brand-green" />
              <div>
                <p className="font-heading text-lg font-bold text-slate-900">Kasiyer Ödeme Ekranı (Demo)</p>
                <p className="text-sm text-slate-500">Kasiyer tutar + ödeme tipi seçer; motor doğru sağlayıcıya yönlendirir.</p>
              </div>
            </div>
            <InfoBox testid="pc-cashier-help" title="Kasiyer bu ekranı nasıl kullanır?" defaultOpen
              example="Kasiyer 250 ₺ için 'Banka Kartı' seçer → motor, kurallarına göre en uygun POS'a yönlendirir. Kasiyerin hangi bankaya gittiğini bilmesine gerek yoktur.">
              Kasiyer yalnızca <b>tutarı</b> ve <b>ödeme tipini</b> (Banka Kartı / Yemek Kartı) seçer.
              Arka plandaki <b>yönlendirme motoru</b> ödemeyi doğru sağlayıcıya/terminale kendisi iletir.
              Bu bir <b>demo</b> ekranıdır (SANDBOX) — gerçek para çekilmez.
            </InfoBox>
            <label className="mb-3 flex cursor-pointer items-center justify-between rounded-lg bg-brand-green/5 px-3 py-2 ring-1 ring-inset ring-brand-green/20">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Coins className="h-4 w-4 text-brand-green" /> Kısmi Ödeme (Kredi Kartı + Yemek Kartı)</span>
              <Switch data-testid="pc-checkout-split-toggle" checked={co.split} onCheckedChange={(v) => { setCo({ ...co, split: v }); setCoResult(null); setSplitResult(null); }} />
            </label>
            {!co.split && (<>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-[11px]">Tutar ({curSym()})</Label>
                <Input type="number" data-testid="pc-checkout-amount" value={co.amount} onChange={(e) => setCo({ ...co, amount: e.target.value })} placeholder="0,00" className="mt-1 text-lg font-bold" />
                <p className="mt-1 text-[11px] text-slate-400">Test: kuruşu <b>.13</b> olan tutar SANDBOX'ta red döner.</p>
              </div>
              <div>
                <Label className="text-[11px]">Terminal (opsiyonel)</Label>
                <select data-testid="pc-checkout-terminal" value={co.terminal_id} onChange={(e) => setCo({ ...co, terminal_id: e.target.value })} className={`mt-1 ${selCls}`}>
                  <option value="">— (varsayılan)</option>{terminals.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button data-testid="pc-checkout-kind-card" onClick={() => setCo({ ...co, payment_kind: "card" })} className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-3 text-sm font-bold transition-colors ${co.payment_kind === "card" ? "border-brand-green bg-brand-green/5 text-brand-green" : "border-slate-200 text-slate-500"}`}><Landmark className="h-5 w-5" /> Banka Kartı</button>
              <button data-testid="pc-checkout-kind-meal" onClick={() => setCo({ ...co, payment_kind: "meal_card" })} className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-3 text-sm font-bold transition-colors ${co.payment_kind === "meal_card" ? "border-brand-green bg-brand-green/5 text-brand-green" : "border-slate-200 text-slate-500"}`}><Utensils className="h-5 w-5" /> Yemek Kartı</button>
            </div>
            {co.payment_kind === "meal_card" && (
              <div className="mt-3">
                <Label className="text-[11px]">Yemek Kartı Markası</Label>
                <select data-testid="pc-checkout-brand" value={co.brand_code} onChange={(e) => setCo({ ...co, brand_code: e.target.value })} className={`mt-1 ${selCls}`}>
                  <option value="">— (ilk etkin yemek kartı)</option>
                  {mealProviders.map((p) => <option key={p.id} value={p.brand_code}>{`${p.name}${p.brand_code ? ` (${p.brand_code})` : ""}`}</option>)}
                </select>
                {mealProviders.length === 0 && <p className="mt-1 text-[11px] text-amber-600">Etkin yemek kartı yok — Sağlayıcılar sekmesinden ekleyin.</p>}
              </div>
            )}
            <label className="mt-3 flex cursor-pointer items-center justify-between rounded-lg bg-slate-50 px-3 py-2 ring-1 ring-inset ring-slate-200">
              <span className="text-sm text-slate-600">Test: bu işlemi zorla reddet (SANDBOX)</span>
              <Switch data-testid="pc-checkout-force" checked={co.force} onCheckedChange={(v) => setCo({ ...co, force: v })} />
            </label>
            <Button data-testid="pc-checkout-submit" onClick={runCheckout} disabled={coBusy} className="mt-4 w-full bg-brand-green py-6 text-base text-white hover:bg-brand-green-600">
              {coBusy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CreditCard className="mr-2 h-5 w-5" />} Ödeme Al
            </Button>
            {coResult && (
              <div data-testid="pc-checkout-result" className={`mt-4 rounded-lg border-2 p-4 ${coResult.status === "captured" ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
                <div className="flex items-center gap-2">
                  {coResult.status === "captured" ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <XCircle className="h-6 w-6 text-red-600" />}
                  <p className={`text-lg font-bold ${coResult.status === "captured" ? "text-emerald-700" : "text-red-700"}`}>{coResult.status === "captured" ? "Ödeme Onaylandı" : "Ödeme Reddedildi"}</p>
                  <span className="ml-auto rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-slate-500 ring-1 ring-inset ring-slate-200">{coResult.intent_no}</span>
                </div>
                <div className="mt-2 grid gap-1 text-sm text-slate-600 sm:grid-cols-2">
                  <p><b>Sağlayıcı:</b> {coResult.provider_name}</p>
                  <p><b>Yönlendirme:</b> {coResult.routed_by}</p>
                  {coResult.auth_code && <p><b>Onay Kodu:</b> <span className="font-mono">{coResult.auth_code}</span></p>}
                  {coResult.masked_pan && <p><b>Kart:</b> <span className="font-mono">{coResult.masked_pan}</span></p>}
                  <p className="sm:col-span-2 text-slate-500">{coResult.message}</p>
                </div>
              </div>
            )}
            </>)}
            {co.split && (
              <div className="space-y-3" data-testid="pc-split-panel">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-[11px] flex items-center gap-1"><Landmark className="h-3.5 w-3.5" /> Kredi / Banka Kartı ({curSym()})</Label>
                    <Input type="number" data-testid="pc-split-card-amount" value={co.card_amount} onChange={(e) => setCo({ ...co, card_amount: e.target.value })} placeholder="0,00" className="mt-1 text-lg font-bold" />
                  </div>
                  <div>
                    <Label className="text-[11px] flex items-center gap-1"><Utensils className="h-3.5 w-3.5" /> Yemek Kartı ({curSym()})</Label>
                    <Input type="number" data-testid="pc-split-meal-amount" value={co.meal_amount} onChange={(e) => setCo({ ...co, meal_amount: e.target.value })} placeholder="0,00" className="mt-1 text-lg font-bold" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px]">Yemek Kartı Markası</Label>
                  <select data-testid="pc-split-meal-brand" value={co.meal_brand} onChange={(e) => setCo({ ...co, meal_brand: e.target.value })} className={`mt-1 ${selCls}`}>
                    <option value="">— (ilk etkin yemek kartı)</option>
                    {mealProviders.map((p) => <option key={p.id} value={p.brand_code}>{`${p.name}${p.brand_code ? ` (${p.brand_code})` : ""}`}</option>)}
                  </select>
                  {mealProviders.length === 0 && <p className="mt-1 text-[11px] text-amber-600">Etkin yemek kartı yok — Sağlayıcılar sekmesinden ekleyin.</p>}
                </div>
                <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm ring-1 ring-inset ring-slate-200">
                  <span className="text-slate-500">Toplam</span>
                  <span className="font-heading text-lg font-bold text-slate-900" data-testid="pc-split-total">{tl((+co.card_amount || 0) + (+co.meal_amount || 0))}</span>
                </div>
                <label className="flex cursor-pointer items-center justify-between rounded-lg bg-slate-50 px-3 py-2 ring-1 ring-inset ring-slate-200">
                  <span className="text-sm text-slate-600">Test: bir parçayı zorla reddet (atomik iptal denemesi)</span>
                  <Switch data-testid="pc-split-force" checked={co.force} onCheckedChange={(v) => setCo({ ...co, force: v })} />
                </label>
                <Button data-testid="pc-split-submit" onClick={runSplit} disabled={coBusy} className="w-full bg-brand-green py-6 text-base text-white hover:bg-brand-green-600">
                  {coBusy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Coins className="mr-2 h-5 w-5" />} Bölünmüş Ödeme Al
                </Button>
                {splitResult && (
                  <div data-testid="pc-split-result" className={`rounded-lg border-2 p-4 ${splitResult.all_ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
                    <div className="flex items-center gap-2">
                      {splitResult.all_ok ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <XCircle className="h-6 w-6 text-red-600" />}
                      <p className={`text-lg font-bold ${splitResult.all_ok ? "text-emerald-700" : "text-red-700"}`}>{splitResult.all_ok ? "Bölünmüş Ödeme Onaylandı" : "Bölünmüş Ödeme Başarısız"}</p>
                      <span className="ml-auto rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-slate-500 ring-1 ring-inset ring-slate-200">{splitResult.group_id}</span>
                    </div>
                    <div className="mt-2 space-y-1">
                      {splitResult.parts.map((pt, i) => {
                        const badge = STATUS_BADGE[pt.status] || [pt.status, "bg-slate-100 text-slate-600"];
                        return (
                          <div key={pt.id || i} className="flex items-center justify-between rounded-md bg-white/70 px-2 py-1 text-sm ring-1 ring-inset ring-slate-200">
                            <span className="text-slate-600">{pt.provider_name}</span>
                            <span className="tabular font-semibold text-slate-800">{tl(pt.amount)}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge[1]}`}>{badge[0]}</span>
                          </div>
                        );
                      })}
                    </div>
                    {!splitResult.all_ok && <p className="mt-2 text-sm text-red-600">{splitResult.failed?.message} · Tahsil edilen parçalar iptal edildi (atomik).</p>}
                  </div>
                )}
              </div>
            )}
          </Section>

          <Section>
            <p className="mb-2 font-heading text-sm font-bold text-slate-900">Son İşlemler (SANDBOX)</p>
            {intents.length === 0 ? <p className="py-4 text-center text-sm text-slate-400">Henüz işlem yok.</p> : (
              <div className="overflow-x-auto" data-testid="pc-intents">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase text-slate-400"><th className="py-2">İşlem No</th><th>Sağlayıcı</th><th className="text-right">Tutar</th><th>Durum</th><th className="text-right">İşlem</th></tr></thead>
                  <tbody>
                    {intents.map((i) => {
                      const badge = STATUS_BADGE[i.status] || [i.status, "bg-slate-100 text-slate-600"];
                      return (
                        <tr key={i.id} data-testid={`pc-intent-${i.id}`} className="border-b border-slate-50">
                          <td className="py-1.5 font-mono text-[11px] text-slate-400">{i.intent_no}</td>
                          <td className="text-slate-700">{i.provider_name}</td>
                          <td className="text-right tabular font-semibold text-slate-800">{tl(i.amount)}</td>
                          <td><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge[1]}`}>{badge[0]}</span></td>
                          <td className="text-right">
                            {i.status === "captured" && (
                              <span className="inline-flex gap-1">
                                <button data-testid={`pc-intent-refund-${i.id}`} onClick={() => reverseIntent(i.id, "refund")} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-amber-700 hover:bg-amber-50"><RotateCcw className="h-3 w-3" /> İade</button>
                                <button data-testid={`pc-intent-void-${i.id}`} onClick={() => reverseIntent(i.id, "void")} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100"><Ban className="h-3 w-3" /> İptal</button>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>
      )}

      {/* ---------------- Mutabakat ---------------- */}
      {sub === "reconciliation" && (
        <Section>
          <div className="mb-3 flex items-center gap-2"><Coins className="h-5 w-5 text-brand-green" /><p className="font-heading text-lg font-bold text-slate-900">Mutabakat (Günlük)</p></div>
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <div><Label className="text-[11px]">Başlangıç</Label><DatePickerTR testid="pc-recon-from" value={reconRange.from} onChange={(v) => setReconRange({ ...reconRange, from: v })} /></div>
            <div><Label className="text-[11px]">Bitiş</Label><DatePickerTR testid="pc-recon-to" value={reconRange.to} onChange={(v) => setReconRange({ ...reconRange, to: v })} /></div>
            <Button data-testid="pc-recon-run" onClick={loadRecon} disabled={reconBusy} className="bg-brand-green text-white hover:bg-brand-green-600">{reconBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Coins className="mr-1.5 h-4 w-4" />} Hesapla</Button>
          </div>
          {recon && (
            <>
              <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="pc-recon-totals">
                <Stat label="İşlem" value={recon.totals.count} />
                <Stat label="Brüt" value={tl(recon.totals.gross)} />
                <Stat label="Komisyon" value={tl(recon.totals.commission)} />
                <Stat label="Net (beklenen)" value={tl(recon.totals.net_expected)} />
              </div>
              {recon.rows.length === 0 ? <p className="py-4 text-center text-sm text-slate-400">Bu aralıkta tahsilat yok.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="pc-recon-table">
                    <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase text-slate-400"><th className="py-2">Sağlayıcı</th><th className="text-right">Adet</th><th className="text-right">Brüt</th><th className="text-right">Kom. %</th><th className="text-right">Komisyon</th><th className="text-right">Net</th></tr></thead>
                    <tbody>
                      {recon.rows.map((r) => (
                        <tr key={r.provider_id} className="border-b border-slate-50">
                          <td className="py-1.5"><span className="inline-flex items-center gap-1">{(() => { const I = KIND_ICON[r.kind] || CreditCard; return <I className="h-3.5 w-3.5 text-slate-400" />; })()}<span className="text-slate-700">{r.provider_name}</span></span></td>
                          <td className="text-right tabular">{r.count}</td>
                          <td className="text-right tabular">{tl(r.gross)}</td>
                          <td className="text-right tabular text-slate-500">%{r.commission_pct}</td>
                          <td className="text-right tabular text-red-600">-{tl(r.commission)}</td>
                          <td className="text-right tabular font-semibold text-emerald-700">{tl(r.net_expected)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Section>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2 ring-1 ring-inset ring-slate-200">
      <p className="text-[11px] font-medium text-slate-500">{label}</p>
      <p className="font-heading text-lg font-bold text-slate-900">{value}</p>
    </div>
  );
}
